import { Box, Button, Tooltip, Typography } from "@mui/material";
import React, { useState, useEffect, useCallback } from "react";
// import CheckIcon from "@mui/icons-material/Check";
import WestIcon from "@mui/icons-material/West";
import NirantaraIcon from "../assets/images/vajra.svg";
import { getConsultantIconUrl } from "../utils/consultantLogo";
import { useLocation, useNavigate } from "react-router-dom";
import {
  miniSidebarElements,
  sidebarElements1,
  sidebarElementsGroupAdmin,
  socialElements,
  sidebarElementsFacility,
} from "../utils/constant";
import { useUser } from "../context/AuthContext";
import SidebarItemsRenderer from "../utils/layoutFunctions";
import {
  filterMiniSidebarElements,
  getRoleBasedSidebar,
} from "../utils/sidebarFunctions";
import socialDataSidebarStyles from "../styles/layout/socialDataSidebar.module.css";
import { get } from "../utils/api";

const SocialLayout = () => {
  const { user } = useUser();
  const location = useLocation();
  const navigate = useNavigate();

  const [isHovered, setIsHovered] = useState(false);
  const [completionStatus, setCompletionStatus] = useState({});

  const sidebarElements = getRoleBasedSidebar({
    user,
    sidebarElements1,
    sidebarElementsFacility,
  });

  const filteredSidebar = sidebarElements.map((item) => {
    if (item.path === "/production-revenue") {
      return {
        ...item,
        heading: user?.companyAdmin ? "Production & Revenue" : "Production",
      };
    }
    if (item.heading === "Setup" && item.subData) {
      const newSubData = item.subData.filter(
        (sub) => sub.primary !== "Facility Master" || !user?.facility,
      );
      return { ...item, subData: newSubData };
    }
    return item;
  });

  const miniSidebarFiltered = filterMiniSidebarElements({
    user,
    miniSidebarElements,
  });

  const allSocialDataModules = [
    { label: "Employee Well-being", slug: "employee-wellbeing" },
    { label: "Diversity and Inclusion", slug: "diversity-and-inclusion" },
    { label: "Community Engagement", slug: "community-engagement" },
    { label: "Labour Practices", slug: "labour-practices" },
    { label: "Health and Safety", slug: "health-safety" },
    { label: "Human Rights", slug: "human-rights" },
    { label: "Training and Development", slug: "training-development" },
    { label: "Employee Benefits", slug: "employee-benefit" },
    { label: "Customer Satisfaction", slug: "customer-satisfaction" },
  ];

  const getSocialDataRouteInfo = () => {
    // Supports:
    // - /social/data
    // - /social/data/<slug>/<id>
    const parts = location.pathname.split("/").filter(Boolean);
    const socialIdx = parts.indexOf("social");
    const dataIdx = parts.indexOf("data");
    if (socialIdx === -1 || dataIdx === -1)
      return { activeSlug: "", id: "", isFormPage: false, module: "" };

    const slug = parts[dataIdx + 1] || "";
    const id = parts[dataIdx + 2] || "";

    // Derive module (annual / monthly):
    // 1. Prefer URL entryType for current page
    // 2. Fallback to localStorage
    let module = "";

    const searchParams = new URLSearchParams(location.search);
    const urlModule = searchParams.get("entryType");
    if (urlModule === "annual") {
      module = "annual";
    } else if (urlModule === "monthly") {
      module = "monthly";
    }

    if (!module) {
      try {
        if (typeof window !== "undefined") {
          const storedRaw = window.localStorage.getItem("socialEntryMeta");
          if (storedRaw) {
            const stored = JSON.parse(storedRaw);
            if (stored?.module === "annual" || stored?.module === "monthly") {
              module = stored.module;
            }
          }
        }
      } catch (e) {
        console.error("Failed to read socialEntryMeta from localStorage", e);
      }
    }

    // Only show sidebar on form pages (when we have a valid module slug)
    const isFormPage =
      !!slug && allSocialDataModules.some((m) => m.slug === slug);
    return { activeSlug: slug, id, isFormPage, module };
  };

  const getVisibleSocialDataModules = (module) => {
    // Rules based on module, same as in WellBeing.jsx:
    // - Monthly: hide "Employee Benefits"
    // - Annual: hide "Training and Development"
    if (module === "annual") {
      return allSocialDataModules.filter(
        (m) => m.slug !== "training-development",
      );
    }
    if (module === "monthly") {
      return allSocialDataModules.filter((m) => m.slug !== "employee-benefit");
    }
    return allSocialDataModules;
  };

  // Map module slugs to possible API keys (supports backend key variants/typos)
  const getModuleDataKeys = (slug) => {
    const mapping = {
      "employee-wellbeing": ["employeeWellBeing"],
      "diversity-and-inclusion": ["diversityAndInclusion"],
      "community-engagement": ["communityEngagement"],
      "labour-practices": ["labourPractices"],
      "health-safety": ["healthAndSafety"],
      "human-rights": ["humanRights"],
      "training-development": [
        "trainingAndDevelopment",
        "trainingAndDevlopment",
      ],
      "employee-benefit": [
        "employeeBenefits",
        "employeeBenifits",
        "employeeBenifites",
      ],
      "customer-satisfaction": ["customerSatisfaction", "customerSatifaction"],
    };
    return mapping[slug] || [];
  };

  const normalizeKey = (key = "") =>
    String(key)
      .toLowerCase()
      .replace(/[^a-z]/g, "");
  const isCompletedFlag = (value) => value === true || value === "true";

  // Check if a module has been completed (has data)
  const checkModuleCompletion = useCallback(
    async (id) => {
      if (!id) {
        setCompletionStatus({});
        return;
      }

      try {
        const response = await get(`/social/getSocial?id=${id}`);
        const responseData = response?.data?.data;
        const socialData = Array.isArray(responseData)
          ? responseData[0]
          : responseData;

        // Get module type from URL, API response, or localStorage
        let module = "";
        const searchParams = new URLSearchParams(location.search);
        const urlModule = searchParams.get("entryType");
        if (urlModule === "annual") {
          module = "annual";
        } else if (urlModule === "monthly") {
          module = "monthly";
        }

        if (!module) {
          const apiModule =
            socialData?.module || socialData?._socialDataRef?.module;
          if (apiModule === "Annual" || apiModule === "annual") {
            module = "annual";
          } else if (apiModule === "Monthly" || apiModule === "monthly") {
            module = "monthly";
          }
        }

        if (!module) {
          try {
            if (typeof window !== "undefined") {
              const storedRaw = window.localStorage.getItem("socialEntryMeta");
              if (storedRaw) {
                const stored = JSON.parse(storedRaw);
                if (
                  stored?.module === "annual" ||
                  stored?.module === "monthly"
                ) {
                  module = stored.module;
                }
              }
            }
          } catch (e) {
            console.error(
              "Failed to read socialEntryMeta from localStorage",
              e,
            );
          }
        }

        const visibleModules = getVisibleSocialDataModules(module);

        const stepsCompleted =
          socialData?.stepsCompleted ||
          socialData?._socialDataRef?.stepsCompleted ||
          {};

        // Check completion status for each visible module
        const status = {};
        visibleModules.forEach((moduleItem) => {
          const dataKeys = getModuleDataKeys(moduleItem.slug);
          if (dataKeys.length > 0) {
            // Resolve module data by exact key OR normalized key match.
            const moduleData =
              dataKeys
                .map((k) => socialData?.[k])
                .find((val) => val !== undefined) ||
              Object.entries(socialData || {}).find(([k]) =>
                dataKeys.some(
                  (candidate) => normalizeKey(candidate) === normalizeKey(k),
                ),
              )?.[1];

            // Helper function to check if a value has meaningful data
            const hasMeaningfulData = (val) => {
              if (val === null || val === undefined || val === "") {
                return false;
              }
              if (typeof val === "object") {
                if (Array.isArray(val)) {
                  // For arrays, check if at least one item has meaningful data
                  return (
                    val.length > 0 &&
                    val.some((item) => hasMeaningfulData(item))
                  );
                } else {
                  // For objects, check if at least one property has meaningful data
                  const keys = Object.keys(val);
                  if (keys.length === 0) return false;
                  return keys.some((key) => hasMeaningfulData(val[key]));
                }
              }
              // For primitives (string, number, boolean), check if not empty
              if (typeof val === "string") {
                return val.trim() !== "";
              }
              if (typeof val === "number") {
                return val !== 0 || val === 0; // 0 is meaningful
              }
              if (typeof val === "boolean") {
                return true; // boolean values are always meaningful
              }
              return true;
            };

            // Consider a module completed by explicit stepsCompleted when present.
            // Only fallback to data-based detection if backend flag is missing.
            const hasData =
              moduleData !== undefined &&
              moduleData !== null &&
              hasMeaningfulData(moduleData);
            const explicitStepValue = (() => {
              for (const k of dataKeys) {
                if (
                  Object.prototype.hasOwnProperty.call(stepsCompleted || {}, k)
                ) {
                  return stepsCompleted[k];
                }
                const normalizedMatch = Object.entries(
                  stepsCompleted || {},
                ).find(
                  ([stepKey]) => normalizeKey(stepKey) === normalizeKey(k),
                );
                if (normalizedMatch) return normalizedMatch[1];
              }
              return undefined;
            })();

            if (explicitStepValue !== undefined && explicitStepValue !== null) {
              status[moduleItem.slug] = isCompletedFlag(explicitStepValue);
            } else {
              status[moduleItem.slug] = hasData;
            }
          } else {
            status[moduleItem.slug] = false;
          }
        });

        setCompletionStatus(status);
      } catch (error) {
        console.error("Error checking module completion:", error);
        setCompletionStatus({});
      }
    },
    [location.search],
  );

  // Fetch completion status when id or location changes
  useEffect(() => {
    const { id } = getSocialDataRouteInfo();
    if (id) {
      checkModuleCompletion(id);
    }
  }, [location.pathname, checkModuleCompletion]);

  const renderSocialDataModulesSidebar = () => {
    const { activeSlug, id, module } = getSocialDataRouteInfo();
    const visibleModules = getVisibleSocialDataModules(module);
    const activeIndex = visibleModules.findIndex((m) => m.slug === activeSlug);

    // Check if previous modules are completed
    const isModuleUnlocked = (idx) => {
      if (idx === 0) return true; // First module is always unlocked
      if (!id) return false; // No id means not unlocked

      // Check if all previous modules are completed
      for (let i = 0; i < idx; i++) {
        const prevModuleSlug = visibleModules[i].slug;
        if (!completionStatus[prevModuleSlug]) {
          return false; // Previous module not completed
        }
      }
      return true; // All previous modules completed
    };

    return (
      <div>
        <ul className={socialDataSidebarStyles.sidebarList}>
          {visibleModules.map((item, idx) => {
            const isActive = item.slug === activeSlug;
            const isUnlocked = isModuleUnlocked(idx);
            const isDisabled = !id || !isUnlocked; // Disabled if no id or not unlocked
            // Only show completed if the module actually has data AND it's not the active page
            // Active page should never show checkmark, only the active blue dot
            const isCompleted =
              !isActive && completionStatus[item.slug] === true;
            const isUpcoming = activeIndex !== -1 && idx > activeIndex;

            return (
              <li
                key={item.slug}
                className={socialDataSidebarStyles.sidebarItemWrapper}
              >
                <div
                  className={[
                    socialDataSidebarStyles.sidebarItemRow,
                    isDisabled
                      ? socialDataSidebarStyles.sidebarItemRowDisabled
                      : "",
                  ].join(" ")}
                  style={{
                    cursor: isDisabled ? "not-allowed" : "pointer",
                  }}
                  onClick={() => {
                    if (isDisabled) return;
                    navigate(`/social/data/${item.slug}/${id}`);
                  }}
                  title={
                    !id
                      ? "Create an entry first"
                      : !isUnlocked
                        ? "Please complete previous modules first"
                        : item.label
                  }
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key !== "Enter") return;
                    if (isDisabled) return;
                    navigate(`/social/data/${item.slug}/${id}`);
                  }}
                >
                  <div
                    className={[
                      socialDataSidebarStyles.dot,
                      isCompleted
                        ? socialDataSidebarStyles.dotChecked
                        : isActive
                          ? socialDataSidebarStyles.dotActive
                          : socialDataSidebarStyles.dotUpcoming,
                    ].join(" ")}
                  >
                    {/* {isCompleted && !isActive && (
                      <CheckIcon className={socialDataSidebarStyles.checkIcon} style={{ width:"1rem" }} />
                    )} */}
                  </div>
                  <Typography
                    className={[
                      socialDataSidebarStyles.text,
                      isActive ? socialDataSidebarStyles.textActive : "",
                    ].join(" ")}
                    sx={{ fontSize: "0.74rem" }}
                  >
                    {item.label}
                  </Typography>
                </div>

                {idx !== visibleModules.length - 1 && (
                  <div className={socialDataSidebarStyles.connector} />
                )}
              </li>
            );
          })}
        </ul>
      </div>
    );
  };

  const renderSidebarItems = (elements) => {
    return elements.map((section, index) => (
      <div
        key={index}
        style={{
          fontFamily: "Inter",
          display: "flex",
          flexDirection: "column",
          gap: "0.5rem",
          marginTop: "1.5rem",
        }}
      >
        <p
          style={{
            color: "#fff",
            fontWeight: "700",
            fontSize: "24px",
            padding: "0 2rem 0.5rem 0.5rem",
            marginLeft: "1rem",
            borderBottom: "2px solid #fff",
            display: "inline-block",
          }}
        >
          Social
        </p>

        {section.subData
          .filter((item) => user?.companyAdmin || item.path !== "/social/data")
          .map((item) => (
            <div key={item.primary} style={{ display: "flex" }}>
              <div
                style={{
                  width: "8px",
                  height: "auto",
                  backgroundColor: location.pathname.startsWith(item.path)
                    ? "#fff"
                    : "transparent",
                  borderRadius: "0 4px 4px 0",
                }}
              ></div>
              <a
                href={item.path}
                key={item.primary}
                style={{
                  textDecoration: "none",
                  color: "#000",
                  padding: "0.4rem 0.75rem",
                  display: "flex",
                  borderRadius: "4px",
                  margin: "0rem 0.75rem 0 0.75rem",
                  width: "100%",
                  backgroundColor: location.pathname.startsWith(item.path)
                    ? "#fff"
                    : "transparent",
                }}
              >
                <Box
                  component="img"
                  src={item.icon}
                  alt={"img"}
                  sx={{
                    color: "red",
                    mr: "8px",
                    filter: location.pathname.startsWith(item.path)
                      ? "invert(0)"
                      : "invert(1)",
                  }}
                />
                <Typography
                  style={{
                    color: location.pathname.startsWith(item.path)
                      ? "rgba(235, 126, 14, 1)"
                      : "#fff",
                    fontFamily: "Inter",
                    fontSize: "16px",
                    fontWeight: "500",
                    lineHeight: "24px",
                  }}
                >
                  {item.primary}
                </Typography>
              </a>
            </div>
          ))}
      </div>
    ));
  };

  const renderMainSidebarItems = (elements) => {
    return elements.map((item, index) => (
      <div
        key={index}
        style={{
          fontFamily: "Inter",
          display: "flex",
          flexDirection: "column",
          marginTop: "0.25rem",
        }}
      >
        <div
          key={index}
          style={{
            display: "flex",
            justifyContent: "center",
            backgroundColor:
              item.primary === "Social"
                ? "rgba(235, 126, 14, 1)"
                : "transparent",
          }}
        >
          <Tooltip
            title={item.title}
            arrow
            placement="bottom-start"
            PopperProps={{
              modifiers: [
                {
                  name: "offset",
                  options: {
                    offset: [0, 0],
                  },
                },
              ],
            }}
            componentsProps={{
              tooltip: {
                sx: {
                  color: "#fff",
                  fontSize: "12px",
                  borderRadius: "4px",
                  padding: "0.25rem 0.5rem",
                },
                arrow: {
                  color: "#fff",
                },
              },
            }}
          >
            <a
              href={item.path}
              key={item.primary}
              style={{
                textDecoration: "none",
                color: "green",
                padding: "0.4rem",
                borderRadius: "4px",
              }}
            >
              <Box
                component="img"
                src={item.icon}
                alt={"img"}
                sx={{
                  width: "24px",
                  height: "24px",
                }}
              />
            </a>
          </Tooltip>
        </div>
      </div>
    ));
  };

  return (
    <>
      <Box style={{ display: "flex", overflow: "hidden", width: "100%" }}>
        <div
          style={{
            width: "22.5%",
            height: "100vh",
            backgroundColor: "#E6E6E6",
            position: "relative",
            overflow: "visible",
          }}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          <Box display="flex" justifyContent="center" py={1}>
            <img
              src={getConsultantIconUrl() || NirantaraIcon}
              alt="Nirantara Logo"
              style={
                getConsultantIconUrl()
                  ? { cursor: "pointer", height: "36px", width: "36px", objectFit: "contain" }
                  : { cursor: "pointer", height: "44px", width: "auto", objectFit: "contain" }
              }
              onClick={() => navigate("/dashboard")}
            />
          </Box>
          <div style={{ width: "100%", marginTop: "1rem" }}>
            {renderMainSidebarItems(miniSidebarFiltered)}
          </div>

          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              width: "21.3vw",
              height: "100vh",
              backgroundColor: "#fff",
              zIndex: 1200,
              overflow: "hidden",
              transform: isHovered ? "translateX(0)" : "translateX(-100%)",
              opacity: isHovered ? 1 : 0,
              transition: "opacity 0.4s ease, transform 0.4s ease",
              pointerEvents: isHovered ? "auto" : "none",
              willChange: "transform, opacity",
            }}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
          >
            {
              <SidebarItemsRenderer
                elements={
                  user?.groupManager
                    ? sidebarElementsGroupAdmin
                    : filteredSidebar
                }
              />
            }
          </div>
        </div>
        <div
          style={{
            backgroundColor: "rgba(235, 126, 14, 1)",
            width: "77.5%",
          }}
        >
          {getSocialDataRouteInfo().isFormPage && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-start",
                alignItems: "center",
                px: 3,
                pt: 2,
              }}
            >
              <Button
                variant="text"
                onClick={() => navigate("/social/data")}
                sx={{
                  textTransform: "none",
                  color: "#ffffff",
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  padding: 0,
                  minWidth: "auto",
                  "&:hover": {
                    backgroundColor: "transparent",
                    // textDecoration: "underline",
                  },
                }}
              >
                <WestIcon sx={{ fontSize: 18, mr: 0.5 }} />
                Back
              </Button>
            </Box>
          )}
          {getSocialDataRouteInfo().isFormPage
            ? renderSocialDataModulesSidebar()
            : renderSidebarItems(socialElements)}
        </div>
      </Box>
    </>
  );
};

export default SocialLayout;
